import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Select } from '@/components/Select';
import { Typography } from '@/components/Typography';
import type { Product } from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { BRACELET_SIZE_NO_ADJUSTMENT } from '@/lib/buckles';
import { generateDataTestId } from '@/lib/tests';
import { commonMessages } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import { zIndex } from '@/theme/zIndex';
import { SizesModal } from '@/views/StrapView/components/Sizes/components';

import { getBraceletSizeOptions } from '../ShoppingBagCard/utils';

import * as M from './messages';
import * as S from './StrapSelect.styles';

interface StrapSelectProps {
  className?: string;
  hasError: boolean;
  line?: Product;
  lineId: string;
}

export const StrapSelect = ({
  className,
  hasError,
  line,
  lineId,
}: StrapSelectProps) => {
  const { formatMessage } = useIntl();
  const getNoAdjustmentOptionLabel = (size: string) => {
    if (size === 'No adjustment') {
      return formatMessage(formMessages.noAdjustment);
    } else {
      return size;
    }
  };
  const [isOpen, setIsOpen] = useState(false);
  const { country } = useRegionProvider();
  const strapSizes = line
    ? getBraceletSizeOptions(country.code, line).map((size) => ({
        label: getNoAdjustmentOptionLabel(size),
        value: size,
      }))
    : [
        {
          value: BRACELET_SIZE_NO_ADJUSTMENT,
          label: formatMessage(formMessages.noAdjustment),
        },
      ];
  const {
    braceletSizeUpdate,
    snapshot: { checkout, isProcessing },
  } = useCheckout();

  const savedSize = checkout?.lines?.find(
    (line) => line.id === lineId,
  )?.relatedBraceletSize;

  const showError = hasError && !savedSize;

  return (
    <>
      <S.Wrapper
        className={className}
        {...generateDataTestId('select', 'strapSize')}
      >
        <Typography data-test-id='StrapSelectTypography' scale='p3'>
          {M.StrapSize}
        </Typography>
        <S.Container data-test-id='StrapSelectContainer'>
          <Select
            data-test-id='StrapSelectSelect'
            hideSelectedOptions
            isDisabled={isProcessing}
            menuPortalTarget={document.body}
            onChange={(e) => e?.value && braceletSizeUpdate(lineId, e.value)}
            options={strapSizes}
            placeholder={formatMessage(commonMessages.select) + '...'}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: zIndex.alwaysVisible }),
            }}
            value={
              savedSize ? { value: savedSize, label: savedSize } : undefined
            }
            variant='default'
          />
        </S.Container>
        <S.Ruler
          data-test-id='StrapSelectRuler'
          onClick={() => setIsOpen(true)}
        />
        <SizesModal
          data-test-id='StrapSelectSizesModal'
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </S.Wrapper>
      {showError && (
        <S.StrapSizeNotSelectedError data-test-id='StrapSelectStrapSizeNotSelectedError'>
          {M.StrapSizeMustBeSelected}
        </S.StrapSizeNotSelectedError>
      )}
    </>
  );
};
